import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Section } from '@dized/ui';
import { getLocaleName } from './LocaleTable/LocaleList';

const ResponsiveTitle = styled.h3`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  margin: 0 0 20px 0;
  word-break: break-word;
  line-height: 1.3;
  
  @media (max-width: 480px) {
    margin: 0 0 15px 0;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  /* Enhanced scrollbar styling */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  min-width: 1400px; /* Adjusted for column changes */
  table-layout: fixed;
  
  /* Optimized column widths for updated columns */
  th:nth-child(1), td:nth-child(1) { width: 10%; } /* Language */
  th:nth-child(2), td:nth-child(2) { width: 8%; }  /* Sessions */
  th:nth-child(3), td:nth-child(3) { width: 8%; }  /* Players */
  th:nth-child(4), td:nth-child(4) { width: 10%; } /* Avg Players/Session */
  th:nth-child(5), td:nth-child(5) { width: 9%; }  /* Intro Completed */
  th:nth-child(6), td:nth-child(6) { width: 9%; }  /* Setup Completed */
  th:nth-child(7), td:nth-child(7) { width: 9%; }  /* First Turn Completed */
  th:nth-child(8), td:nth-child(8) { width: 10%; } /* Teaching Completed */
  th:nth-child(9), td:nth-child(9) { width: 10%; } /* Gameplay Completed */
  th:nth-child(10), td:nth-child(10) { width: 9%; } /* Tutorial Completed */
  th:nth-child(11), td:nth-child(11) { width: 8%; } /* Avg Time */
  th:nth-child(12), td:nth-child(12) { width: 10%; } /* Total Time */

  /* Mobile responsive adjustments */
  @media (max-width: 1200px) {
    min-width: 1200px;
    font-size: 0.9rem;
    
    th, td {
      padding: 10px 6px;
    }
  }

  @media (max-width: 768px) {
    min-width: 1000px;
    font-size: 0.85rem;
    
    th, td {
      padding: 8px 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  @media (max-width: 480px) {
    min-width: 900px;
    font-size: 0.8rem;
    
    th, td {
      padding: 6px 3px;
    }
  }
`;

const TableHeader = styled.thead`
  background: linear-gradient(90deg, #f5f5f5, #e8e8e8);
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: #fafafa;
  }
  
  &:hover {
    background: #f0f7ff;
  }
`;

const TableHeaderCell = styled.th`
  padding: 14px 12px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #ddd;
  font-size: 0.95rem;
  white-space: normal;
  vertical-align: middle;
  line-height: 1.3;
  word-break: break-word;
  
  &:nth-child(2), &:nth-child(3), &:nth-child(4), 
  &:nth-child(5), &:nth-child(6), &:nth-child(7), 
  &:nth-child(8), &:nth-child(9), &:nth-child(10), 
  &:nth-child(11), &:nth-child(12) {
    text-align: center;
  }

  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    padding: 10px 6px;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 8px 4px;
    font-size: 0.8rem;
  }
`;

const TableCell = styled.td`
  padding: 14px 12px;
  border-bottom: 1px solid #eee;
  font-size: 0.95rem;
  color: #555;
  vertical-align: middle;
  line-height: 1.4;
  
  &.number {
    text-align: center;
    font-family: 'Monaco', 'Menlo', monospace;
    font-weight: 500;
  }
  
  &.percentage {
    text-align: center;
    font-weight: 600;
    color: #2196F3;
  }
  
  &.center {
    text-align: center;
  }

  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    padding: 10px 6px;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 8px 4px;
    font-size: 0.8rem;
  }
`;

const formatTime = (seconds) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  
  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${totalSeconds}s`;
};

const formatAvgTime = (seconds) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  
  const totalSeconds = Math.round(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return `${totalSeconds}s`;
};

const formatNumber = (num) => {
  if (num === null || num === undefined) return 'N/A';
  return num.toLocaleString();
};

const formatPercentage = (num) => {
  if (num === null || num === undefined) return 'N/A';
  return `${num.toFixed(1)}%`;
};

// Helper function to aggregate data across all languages
const aggregateStatsData = (dataArray) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) return null;
  
  // If only one language, return it directly
  if (dataArray.length === 1) return dataArray[0];
  
  // Calculate totals
  let totalPlayers = 0;
  let totalSessions = 0;
  let totalTime = 0;
  let totalBlocks = 0;
  let totalPlayerCountBasedOn = 0;
  
  dataArray.forEach(langData => {
    totalPlayers += langData.total_player_count || 0;
    totalSessions += langData.session_count || 0;
    totalTime += langData.total_time_played || 0;
    totalBlocks += langData.total_blocks_advanced || 0;
    totalPlayerCountBasedOn += langData.total_player_count_based_on || 0;
  });
  
  // Calculate average time per session
  const avgTimePerSession = totalSessions > 0 ? totalTime / totalSessions : 0;
  
  // Calculate weighted averages for completion percentages and median data
  const aggregated = {
    locale: `All Languages (${dataArray.length} locales)`,
    total_player_count: totalPlayers,
    session_count: totalSessions,
    total_time_played: totalTime,
    total_blocks_advanced: totalBlocks,
    total_player_count_based_on: totalPlayerCountBasedOn,
    avg_time_played: avgTimePerSession
  };
  
  // State completion percentages - weighted by player count  
  const states = ['intro', 'setup', 'teaching', 'firstturn', 'gameplay', 'tutorial'];
  states.forEach(state => {
    let weightedPercentage = 0;
    let weightedMedianTime = 0;
    let weightedMedianBlocks = 0;
    
    dataArray.forEach(langData => {
      const playerWeight = totalPlayers > 0 ? (langData.total_player_count || 0) / totalPlayers : 0;
      weightedPercentage += (langData[`state_${state}_completed_percentage`] || 0) * playerWeight;
      weightedMedianTime += (langData[`state_${state}_median_time_played`] || 0) * playerWeight;
      weightedMedianBlocks += (langData[`state_${state}_median_blocks_advanced`] || 0) * playerWeight;
    });
    
    aggregated[`state_${state}_completed_percentage`] = weightedPercentage;
    aggregated[`state_${state}_median_time_played`] = weightedMedianTime;
    aggregated[`state_${state}_median_blocks_advanced`] = weightedMedianBlocks;
  });
  
  return aggregated;
};

export const StatsOverview = ({ data, game }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Section>
        <ResponsiveTitle>Stats Overview</ResponsiveTitle>
        <p>No detailed statistics available</p>
      </Section>
    );
  }

  // Calculate average players per session using "Set Player" events
  const calculateAvgPlayersPerSession = (statsData) => {
    // Use the new "Set Player" events field for accurate calculation
    if (!statsData.total_player_count_based_on || statsData.total_player_count_based_on === 0) {
      return 'N/A';
    }
    
    const avgPlayers = statsData.total_player_count / statsData.total_player_count_based_on;
    
    return avgPlayers.toFixed(1);
  };

  // Get aggregated data for detailed section
  const aggregatedData = aggregateStatsData(data);

  // Sort languages by session count (highest first)
  const sortedData = [...data].sort((a, b) => (b.session_count || 0) - (a.session_count || 0));

  return (
    <Section>
      <ResponsiveTitle>Stats Overview</ResponsiveTitle>
      
      <TableContainer>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Language</TableHeaderCell>
              <TableHeaderCell>Sessions</TableHeaderCell>
              <TableHeaderCell>Players</TableHeaderCell>
              <TableHeaderCell>Avg Players/Session</TableHeaderCell>
              <TableHeaderCell>Intro Completed</TableHeaderCell>
              <TableHeaderCell>Setup Completed</TableHeaderCell>
              <TableHeaderCell>First Turn Completed</TableHeaderCell>
              <TableHeaderCell>Teaching Completed</TableHeaderCell>
              <TableHeaderCell>Gameplay Completed</TableHeaderCell>
              <TableHeaderCell>Tutorial Completed</TableHeaderCell>
              <TableHeaderCell>Avg Time</TableHeaderCell>
              <TableHeaderCell>Total Time</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <tbody>
            {sortedData.map((statsData, index) => (
              <TableRow key={`${statsData.locale || 'unknown'}-${index}`}>
                <TableCell>{getLocaleName(statsData.locale) || statsData.locale || 'N/A'}</TableCell>
                <TableCell className="number">{formatNumber(statsData.session_count)}</TableCell>
                <TableCell className="number">{formatNumber(statsData.total_player_count)}</TableCell>
                <TableCell className="number">
                  {calculateAvgPlayersPerSession(statsData)}
                </TableCell>
                <TableCell className="percentage">
                  {formatPercentage(statsData.state_intro_completed_percentage)}
                </TableCell>
                <TableCell className="percentage">
                  {formatPercentage(statsData.state_setup_completed_percentage)}
                </TableCell>
                <TableCell className="percentage">
                  {formatPercentage(statsData.state_firstturn_completed_percentage)}
                </TableCell>
                <TableCell className="percentage">
                  {formatPercentage(statsData.state_teaching_completed_percentage)}
                </TableCell>
                <TableCell className="percentage">
                  {formatPercentage(statsData.state_gameplay_completed_percentage)}
                </TableCell>
                <TableCell className="percentage">
                  {formatPercentage(statsData.state_tutorial_completed_percentage)}
                </TableCell>
                <TableCell className="number">
                  {formatAvgTime(statsData.avg_time_played)}
                </TableCell>
                <TableCell className="number">
                  {formatTime(statsData.total_time_played)}
                </TableCell>
              </TableRow>
            ))}
            {aggregatedData && (
              <TableRow style={{ backgroundColor: '#f0f0f0' }}>
                <TableCell style={{ fontWeight: 'bold' }}>{aggregatedData.locale}</TableCell>
                <TableCell className="number" style={{ fontWeight: 'bold' }}>{formatNumber(aggregatedData.session_count)}</TableCell>
                <TableCell className="number" style={{ fontWeight: 'bold' }}>{formatNumber(aggregatedData.total_player_count)}</TableCell>
                <TableCell className="number" style={{ fontWeight: 'bold' }}>
                  {calculateAvgPlayersPerSession(aggregatedData)}
                </TableCell>
                <TableCell className="percentage" style={{ fontWeight: 'bold' }}>
                  {formatPercentage(aggregatedData.state_intro_completed_percentage)}
                </TableCell>
                <TableCell className="percentage" style={{ fontWeight: 'bold' }}>
                  {formatPercentage(aggregatedData.state_setup_completed_percentage)}
                </TableCell>
                <TableCell className="percentage" style={{ fontWeight: 'bold' }}>
                  {formatPercentage(aggregatedData.state_firstturn_completed_percentage)}
                </TableCell>
                <TableCell className="percentage" style={{ fontWeight: 'bold' }}>
                  {formatPercentage(aggregatedData.state_teaching_completed_percentage)}
                </TableCell>
                <TableCell className="percentage" style={{ fontWeight: 'bold' }}>
                  {formatPercentage(aggregatedData.state_gameplay_completed_percentage)}
                </TableCell>
                <TableCell className="percentage" style={{ fontWeight: 'bold' }}>
                  {formatPercentage(aggregatedData.state_tutorial_completed_percentage)}
                </TableCell>
                <TableCell className="number" style={{ fontWeight: 'bold' }}>
                  {formatAvgTime(aggregatedData.avg_time_played)}
                </TableCell>
                <TableCell className="number" style={{ fontWeight: 'bold' }}>
                  {formatTime(aggregatedData.total_time_played)}
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
      </TableContainer>
      
    </Section>
  );
};

StatsOverview.propTypes = {
  data: PropTypes.array,
  game: PropTypes.shape({
    player_min: PropTypes.number
  })
};

StatsOverview.displayName = 'StatsOverview';
